/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Typography2 } from '@coursera/cds-core';

import { PERSONALIZATION_TAGS } from 'bundles/enterprise-admin-messaging-v2/utils/personalizationTags';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  /** Inserts the tag's display token into the message body at the caret. */
  onInsert: (text: string) => void;
  /** When false, the [Enrollment] tag is hidden. Defaults to true. */
  showEnrollmentTag?: boolean;
};

const styles = {
  root: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
  `,
  // Chip per design: neutral/stroke/primaryWeak border, interactive/secondary
  // text (the label uses v2/Action Secondary via the small-button typography).
  chip: css`
    display: inline-flex;
    align-items: center;
    padding: var(--cds-spacing-50) var(--cds-spacing-100);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-100);
    background: transparent;
    color: var(--cds-color-interactive-secondary);
    cursor: pointer;

    &:hover {
      background: var(--cds-color-neutral-background-primary-weak);
    }

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-primary);
      outline-offset: 1px;
    }
  `,
};

/**
 * "Add tag" control rendered on the message editor's toolbar row. Each chip
 * inserts its personalization token (e.g. `[Learner name]`) at the caret. Pass
 * to `MessageBodyEditor`'s `renderToolbarActions`.
 */
const PersonalizationTagActions = ({ onInsert, showEnrollmentTag = true }: Props) => {
  const visibleTags = showEnrollmentTag
    ? PERSONALIZATION_TAGS
    : PERSONALIZATION_TAGS.filter((tag) => tag.id !== 'ENROLLMENT_NAME');

  return (
    <div css={styles.root}>
      <Typography2 component="span" variant="bodySecondary" color="supportText">
        {_t('Add tag')}
      </Typography2>
      {visibleTags.map((tag) => (
        <button
          key={tag.id}
          type="button"
          css={styles.chip}
          onClick={() => onInsert(tag.display)}
          data-testid={`message-body-add-tag-${tag.id}`}
        >
          <Typography2 component="span" variant="actionSecondary" color="inherit">
            {tag.display}
          </Typography2>
        </button>
      ))}
    </div>
  );
};

export default PersonalizationTagActions;
